import React from 'react';
import { create } from 'zustand';
import { getFirestore, doc, onSnapshot, setDoc, Unsubscribe } from 'firebase/firestore';
import { firebaseApp, firestore } from 'app';
import { WorkingHours } from 'types';
import { toast } from 'sonner';

interface WorkingHoursState {
  // Data
  workingHours: Record<string, WorkingHours | null>; // staffId -> WorkingHours (null for casual employees)
  loading: Record<string, boolean>; // staffId -> loading state
  errors: Record<string, string | null>; // staffId -> error message
  
  // Firestore listeners
  listeners: Record<string, Unsubscribe>; // staffId -> unsubscribe function
  // Refcount of components currently subscribed per staffId. Only when this
  // hits 0 do we tear down the underlying Firestore listener — this avoids
  // both the "listener-per-mount" leak AND the "kill listener while other
  // component still needs it" race.
  refCounts: Record<string, number>;
  
  // Actions
  subscribeToWorkingHours: (staffId: string) => void;
  updateWorkingHours: (staffId: string, workingHours: WorkingHours) => Promise<boolean>;
  unsubscribeFromWorkingHours: (staffId: string) => void;
  clearWorkingHours: (staffId: string) => void;
  clearAllWorkingHours: () => void;
}

const DEFAULT_WORKING_HOURS: WorkingHours = {
  monday: { start: '09:00', end: '17:00', isWorking: true, mealBreakStart: '12:30', mealBreakDuration: 30, actualWorkingHours: 7.5 },
  tuesday: { start: '09:00', end: '17:00', isWorking: true, mealBreakStart: '12:30', mealBreakDuration: 30, actualWorkingHours: 7.5 },
  wednesday: { start: '09:00', end: '17:00', isWorking: true, mealBreakStart: '12:30', mealBreakDuration: 30, actualWorkingHours: 7.5 },
  thursday: { start: '09:00', end: '17:00', isWorking: true, mealBreakStart: '12:30', mealBreakDuration: 30, actualWorkingHours: 7.5 },
  friday: { start: '09:00', end: '17:00', isWorking: true, mealBreakStart: '12:30', mealBreakDuration: 30, actualWorkingHours: 7.5 },
  saturday: { start: '09:00', end: '17:00', isWorking: false, mealBreakStart: '12:30', mealBreakDuration: 30, actualWorkingHours: 0 },
  sunday: { start: '09:00', end: '17:00', isWorking: false, mealBreakStart: '12:30', mealBreakDuration: 30, actualWorkingHours: 0 },
};

// Represents all hours being available (24/7) - used when no working hours are defined
// This is appropriate for casual staff and others without specific working hour constraints
const ALL_HOURS_AVAILABLE: WorkingHours = {
  monday: { start: '00:00', end: '23:59', isWorking: true, mealBreakDuration: 0, actualWorkingHours: 24 },
  tuesday: { start: '00:00', end: '23:59', isWorking: true, mealBreakDuration: 0, actualWorkingHours: 24 },
  wednesday: { start: '00:00', end: '23:59', isWorking: true, mealBreakDuration: 0, actualWorkingHours: 24 },
  thursday: { start: '00:00', end: '23:59', isWorking: true, mealBreakDuration: 0, actualWorkingHours: 24 },
  friday: { start: '00:00', end: '23:59', isWorking: true, mealBreakDuration: 0, actualWorkingHours: 24 },
  saturday: { start: '00:00', end: '23:59', isWorking: true, mealBreakDuration: 0, actualWorkingHours: 24 },
  sunday: { start: '00:00', end: '23:59', isWorking: true, mealBreakDuration: 0, actualWorkingHours: 24 },
};

export const useWorkingHoursStore = create<WorkingHoursState>((set, get) => ({
  workingHours: {},
  loading: {},
  errors: {},
  listeners: {},
  refCounts: {},

  subscribeToWorkingHours: (staffId: string) => {
    const { listeners, workingHours, refCounts } = get();

    // Increment refcount for this staffId.
    set(state => ({
      refCounts: { ...state.refCounts, [staffId]: (state.refCounts[staffId] || 0) + 1 },
    }));

    // If already subscribed, don't subscribe again (data is shared).
    if (listeners[staffId]) {
      return;
    }

    // If working hours already loaded, no need to resubscribe
    if (workingHours[staffId] !== undefined) {
      return;
    }

    // Set loading state
    set(state => ({
      loading: { ...state.loading, [staffId]: true },
      errors: { ...state.errors, [staffId]: null }
    }));

    try {
      const db = firestore;
      const workingHoursDocRef = doc(db, 'workingHours', staffId);

      const unsubscribe = onSnapshot(
        workingHoursDocRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data() as WorkingHours;
            set(state => ({
              workingHours: { ...state.workingHours, [staffId]: data },
              loading: { ...state.loading, [staffId]: false },
              errors: { ...state.errors, [staffId]: null }
            }));
          } else {
            // Document doesn't exist - default to all hours available
            // This is appropriate for casual staff and others without defined working hours
            set(state => ({
              workingHours: { ...state.workingHours, [staffId]: ALL_HOURS_AVAILABLE },
              loading: { ...state.loading, [staffId]: false },
              errors: { ...state.errors, [staffId]: null }
            }));
          }
        },
        (error) => {
          console.error(`Error listening to working hours for staff ${staffId}:`, error);
          // On error, default to all hours available rather than blocking the staff
          if (error.code === 'permission-denied') {
            set(state => ({
              workingHours: { ...state.workingHours, [staffId]: ALL_HOURS_AVAILABLE },
              loading: { ...state.loading, [staffId]: false },
              errors: { ...state.errors, [staffId]: 'permission-denied' }
            }));
          } else {
            set(state => ({
              workingHours: { ...state.workingHours, [staffId]: ALL_HOURS_AVAILABLE },
              loading: { ...state.loading, [staffId]: false },
              errors: { ...state.errors, [staffId]: error.message }
            }));
          }
        }
      );

      // Store the unsubscribe function
      set(state => ({
        listeners: { ...state.listeners, [staffId]: unsubscribe }
      }));
    } catch (error) {
      console.error(`Error setting up working hours listener for staff ${staffId}:`, error);
      set(state => ({
        workingHours: { ...state.workingHours, [staffId]: ALL_HOURS_AVAILABLE },
        loading: { ...state.loading, [staffId]: false },
        errors: { ...state.errors, [staffId]: 'setup-error' }
      }));
    }
  },

  updateWorkingHours: async (staffId: string, workingHours: WorkingHours): Promise<boolean> => {
    try {
      const db = firestore;
      const workingHoursDocRef = doc(db, 'workingHours', staffId);

      // Archive the OUTGOING roster to workingHoursHistory (server-side,
      // admin SDK) so past fortnights are checked against the roster
      // that was in force at the time — skips no-op saves.
      try {
        const { snapshotWorkingHoursHistory } = await import('./contractDriftApi');
        await snapshotWorkingHoursHistory(staffId, workingHours);
      } catch (histErr) {
        console.warn('Working hours history snapshot failed:', histErr);
      }

      // Optimistic update
      set(state => ({
        workingHours: { ...state.workingHours, [staffId]: workingHours }
      }));

      await setDoc(workingHoursDocRef, { ...workingHours, rosterUpdatedAt: new Date() } as any, { merge: true });
      
      toast.success('Working hours updated successfully');
      return true;
    } catch (error) {
      console.error(`Error updating working hours for staff ${staffId}:`, error);
      toast.error('Failed to update working hours');
      
      // Revert optimistic update on error
      const { workingHours: currentHours } = get();
      if (currentHours[staffId]) {
        // Re-subscribe to get the current state from Firestore
        get().subscribeToWorkingHours(staffId);
      }
      
      return false;
    }
  },

  unsubscribeFromWorkingHours: (staffId: string) => {
    const { listeners, refCounts } = get();
    const next = Math.max(0, (refCounts[staffId] || 0) - 1);
    set(state => ({
      refCounts: { ...state.refCounts, [staffId]: next },
    }));
    // Only actually kill the underlying Firestore listener when the last
    // consumer goes away — anything else still using this staffId keeps
    // working.
    if (next > 0) return;

    const unsubscribe = listeners[staffId];
    if (unsubscribe) {
      unsubscribe();
      set(state => {
        const newListeners = { ...state.listeners };
        delete newListeners[staffId];
        return { listeners: newListeners };
      });
    }
  },

  clearWorkingHours: (staffId: string) => {
    const { unsubscribeFromWorkingHours } = get();
    unsubscribeFromWorkingHours(staffId);
    
    set(state => {
      const newWorkingHours = { ...state.workingHours };
      const newLoading = { ...state.loading };
      const newErrors = { ...state.errors };
      
      delete newWorkingHours[staffId];
      delete newLoading[staffId];
      delete newErrors[staffId];
      
      return {
        workingHours: newWorkingHours,
        loading: newLoading,
        errors: newErrors
      };
    });
  },

  clearAllWorkingHours: () => {
    const { listeners } = get();
    
    // Unsubscribe from all listeners
    Object.values(listeners).forEach(unsubscribe => unsubscribe());
    
    set({
      workingHours: {},
      loading: {},
      errors: {},
      listeners: {}
    });
  }
}));

// Helper hook to get working hours for a specific staff member
export const useStaffWorkingHours = (staffId: string | null) => {
  const {
    workingHours,
    loading,
    errors,
    subscribeToWorkingHours,
    updateWorkingHours,
    unsubscribeFromWorkingHours
  } = useWorkingHoursStore();

  // Subscribe when staffId changes, and ALWAYS unsubscribe on unmount /
  // staffId change. The store refcounts internally, so this is safe — it
  // only tears down the underlying Firestore listener when the last
  // consumer is gone.
  React.useEffect(() => {
    if (staffId) {
      subscribeToWorkingHours(staffId);
      return () => {
        unsubscribeFromWorkingHours(staffId);
      };
    }
  }, [staffId, subscribeToWorkingHours, unsubscribeFromWorkingHours]);

  return {
    workingHours: staffId ? workingHours[staffId] : null,
    loading: staffId ? (loading[staffId] ?? false) : false,
    error: staffId ? (errors[staffId] ?? null) : null,
    updateWorkingHours: staffId ? (hours: WorkingHours) => updateWorkingHours(staffId, hours) : () => Promise.resolve(false),
    unsubscribe: staffId ? () => unsubscribeFromWorkingHours(staffId) : () => {}
  };
};
